import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { getPost, POSTS } from "@/data/blog";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    image: [post.hero],
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: "Jersey Blend" },
    publisher: {
      "@type": "Organization",
      name: "Jersey Blend",
      logo: { "@type": "ImageObject", url: "https://jerseyblend.net/og-image.jpg" },
    },
    mainEntityOfPage: `https://jerseyblend.net/blog/${post.slug}`,
  };

  return (
    <div className="bg-background min-h-screen" data-testid={`blog-post-${slug}`}>
      <Seo
        title={post.seoTitle}
        description={post.description}
        keywords={post.keywords}
        path={`/blog/${post.slug}`}
        image={post.hero}
        jsonLd={jsonLd}
      />
      <article className="max-w-3xl mx-auto px-6 md:px-8 pt-12 pb-20">
        <Link to="/blog" className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest mb-8 text-white/70 hover:text-primary">
          <ArrowLeft className="w-4 h-4" /> All guides
        </Link>
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-primary mb-4">Guide</p>
        <h1 className="font-anton text-4xl sm:text-5xl uppercase leading-none text-white">{post.title}</h1>
        <div className="aspect-[16/9] overflow-hidden bg-neutral-900 mt-8 border border-white/10">
          <img src={post.hero} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="mt-10 space-y-8">
          {post.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-anton text-2xl uppercase tracking-tight text-white mb-3">{s.h}</h2>
              {s.p?.map((para, j) => (
                <p key={j} className="text-white/70 leading-relaxed mb-3">{para}</p>
              ))}
              {s.list && (
                <ul className="list-disc pl-5 space-y-2 text-white/70">
                  {s.list.map((li, k) => (
                    <li key={k}>{li}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 border border-primary/30 bg-primary/5 p-8 text-center">
          <p className="font-anton text-2xl uppercase text-white">Ready to build yours?</p>
          <Link
            to={post.related.to}
            data-testid="blog-cta"
            className="inline-flex items-center gap-2 mt-5 bg-primary text-black px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm hover:bg-white transition-colors"
          >
            {post.related.label} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Related guides */}
        {others.length > 0 && (
          <div className="mt-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-6">More guides</p>
            <div className="grid gap-6 sm:grid-cols-2">
              {others.map((o) => (
                <Link key={o.slug} to={`/blog/${o.slug}`} className="group block border border-white/10 bg-card hover:border-primary transition-colors p-5">
                  <h3 className="font-anton text-lg uppercase tracking-tight text-white group-hover:text-primary transition-colors">{o.title}</h3>
                  <p className="text-white/50 text-sm mt-2 line-clamp-2">{o.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
